import { router } from "expo-router";
import { ActionButton } from "./ui/action-button";
import { ICall } from "../types/call";
import { displayPrice } from "../utils/price-helpers";
import { formatPhoneNumber } from "react-phone-number-input";
import { usePostHog } from "posthog-js/react";
import { useFormattedDate } from "../hooks/useFormattedDate";
import { isCallCompleted } from "../utils/call-helpers";
import { Image } from "react-native";
import { ArrowRight, Phone, ShoppingBag, Truck } from "lucide-react-native";

const liveIcon = require("../assets/live.svg");

const getInteractionDetails = (interaction: ICall) => {
  if (interaction.order) {
    const isDelivery = interaction.order.deliveryAddress !== null;
    return {
      icon: isDelivery ? Truck : ShoppingBag,
      color: isDelivery ? "text-purple-500" : "text-green-600",
      borderColor: isDelivery ? "border-purple-500" : "border-green-600",
    };
  }
  return {
    icon: Phone,
    color: "text-blue-500",
    borderColor: "border-blue-300",
  };
};

const NUMBER_OF_CALLS_TO_SHOW = 3;

const RecentInteractions = ({
  calls,
  loading = false,
}: {
  calls: ICall[];
  loading?: boolean;
}) => {
  const posthog = usePostHog();
  const { formatTimeAgo } = useFormattedDate();

  const handleCallClick = (call: ICall) => {
    posthog.capture("recent_call_clicked", {
      call_id: call.id,
      has_order: !!call.order,
      order_total: call.order?.total,
      customer_name: call.customerName,
      phone_number: call.phone,
    });
    router.push(`/calls-and-orders?callId=${call.id}`);
  };

  const handleViewAllCallsClick = () => {
    posthog.capture("view_all_calls_pressed", {
      from_page: "home",
    });
    router.push("/calls-and-orders");
  };

  return (
    <div className="w-full p-6 bg-white border border-gray-200 rounded-lg text-gray-800 max-h-[370px] h-[370px]">
      <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
      <div className="space-y-3">
        {loading ? (
          // Loading skeleton
          <>
            {[...Array(NUMBER_OF_CALLS_TO_SHOW)].map((_, index) => (
              <div
                key={index}
                className="flex items-center space-x-4 p-3 bg-gray-50 rounded-md animate-pulse h-[70px]"
              >
                <div className="w-5 h-5 bg-gray-300 rounded" />
                <div className="flex-grow space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-3/4" />
                  <div className="h-3 bg-gray-300 rounded w-1/2" />
                </div>
                <div className="text-right space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-16 ml-auto" />
                  <div className="h-3 bg-gray-300 rounded w-12 ml-auto" />
                </div>
              </div>
            ))}
          </>
        ) : calls.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Phone size={48} className="text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm font-medium">No recent calls</p>
            <p className="text-gray-400 text-xs mt-1">
              Calls will appear here once you start receiving them
            </p>
          </div>
        ) : (
          <>
            {calls.slice(0, NUMBER_OF_CALLS_TO_SHOW).map((call, index) => {
              const details = getInteractionDetails(call);
              const isCompleted = isCallCompleted(call);
              return (
                <div
                  key={index}
                  className={`flex items-center space-x-4 p-3 bg-gray-50 rounded-md transition-all cursor-pointer h-[70px] hover:bg-gray-100 ${details.borderColor}`}
                  onClick={() => handleCallClick(call)}
                >
                  <details.icon size={20} className={details.color} />
                  <div className="flex-grow">
                    <>
                      <p className="font-semibold text-gray-800 text-[15px]">
                        {formatPhoneNumber(call.phone)}
                      </p>
                      {call.customerName && (
                        <p className="text-sm text-gray-500">
                          {call.customerName}
                        </p>
                      )}
                    </>
                  </div>
                  <div className="text-right flex-shrink-0 gap-0.5 flex flex-col">
                    {call.order ? (
                      <p className="font-semibold text-gray-800 text-[15px]">
                        {displayPrice(call.order.total)}
                      </p>
                    ) : (
                      <></>
                    )}
                    <div>
                      <p
                        className={`text-xs flex flex-row items-center gap-1 ${
                          isCompleted
                            ? "text-gray-400"
                            : "text-blue-600 font-semibold bg-blue-100 px-3 py-1 rounded-full animate-pulse"
                        }`}
                      >
                        {isCompleted
                          ? formatTimeAgo(call.timestamp)
                          : "Ongoing"}
                        {!isCompleted && (
                          <Image
                            source={liveIcon}
                            className="w-5 h-5"
                            style={{
                              width: 20,
                              height: 20,
                              tintColor: "#2563eb",
                            }}
                            resizeMode="contain"
                          />
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="pt-0">
              <ActionButton
                label="View All Calls"
                onPress={handleViewAllCallsClick}
                iconRight={ArrowRight}
                variant="ghost"
                className="w-full text-muted-foreground"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecentInteractions;
